import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LS_WORKSPACE_KEY = 'op_active_workspace_id';

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Global axios interceptor — every API request automatically carries the
// active workspace id via the `x-workspace-id` header. The backend uses
// this for tenant scoping (see /app/backend/workspace.py).
const _setAxiosWorkspaceHeader = (wid) => {
  if (wid) {
    axios.defaults.headers.common['x-workspace-id'] = wid;
  } else {
    delete axios.defaults.headers.common['x-workspace-id'];
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Workspace state — multi-tenancy
  const [activeWorkspaceId, setActiveWorkspaceId] = useState(() => {
    try { return localStorage.getItem(LS_WORKSPACE_KEY) || ''; } catch (_) { return ''; }
  });
  const [activeWorkspace, setActiveWorkspace] = useState(null);
  const [activePlan, setActivePlan] = useState(null);
  const [myWorkspaces, setMyWorkspaces] = useState([]);

  // Push header into axios any time the active workspace changes
  useEffect(() => { _setAxiosWorkspaceHeader(activeWorkspaceId); }, [activeWorkspaceId]);

  useEffect(() => {
    checkAuth();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const _bootstrapWorkspaces = useCallback(async (authedUser) => {
    if (!authedUser) return;
    // Pick default workspace if we don't have one cached
    let wid = activeWorkspaceId;
    const userWsIds = Array.isArray(authedUser.workspace_ids) ? authedUser.workspace_ids : [];
    if (!wid || !userWsIds.includes(wid)) {
      wid = authedUser.default_workspace_id || userWsIds[0] || '';
      if (wid) {
        setActiveWorkspaceId(wid);
        try { localStorage.setItem(LS_WORKSPACE_KEY, wid); } catch (_) { /* noop */ }
      }
    }
    _setAxiosWorkspaceHeader(wid);
    try {
      const [listRes, curRes] = await Promise.all([
        axios.get(`${API_URL}/api/workspaces`, { withCredentials: true }).catch(() => ({ data: [] })),
        wid
          ? axios.get(`${API_URL}/api/workspaces/current`, { withCredentials: true }).catch(() => ({ data: null }))
          : Promise.resolve({ data: null }),
      ]);
      setMyWorkspaces(listRes.data || []);
      if (curRes && curRes.data) {
        setActiveWorkspace(curRes.data.workspace || null);
        setActivePlan(curRes.data.plan || null);
      }
    } catch (_) { /* ignore — endpoints may not be reachable during initial bootstrap */ }
  }, [activeWorkspaceId]);

  const checkAuth = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/auth/me`, {
        withCredentials: true,
      });
      setUser(data);
      await _bootstrapWorkspaces(data);
    } catch (error) {
      setUser(false);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const res = await axios.post(
      `${API_URL}/api/auth/login`,
      { email, password },
      { withCredentials: true }
    );
    const data = res.data;
    setUser(data);
    await _bootstrapWorkspaces(data);
    return data;
  };

  const signup = async (payload) => {
    // payload = { name, email, password, workspace_name, plan_code, company_phone }
    const { data } = await axios.post(
      `${API_URL}/api/auth/signup`,
      payload,
      { withCredentials: true }
    );
    setUser(data);
    if (data.default_workspace_id) {
      setActiveWorkspaceId(data.default_workspace_id);
      try { localStorage.setItem(LS_WORKSPACE_KEY, data.default_workspace_id); } catch (_) { /* noop */ }
    }
    await _bootstrapWorkspaces(data);
    return data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Logout error:', error);
    }
    setUser(false);
    setActiveWorkspaceId('');
    setActiveWorkspace(null);
    setActivePlan(null);
    setMyWorkspaces([]);
    try { localStorage.removeItem(LS_WORKSPACE_KEY); } catch (_) { /* noop */ }
  };

  const refreshUser = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/auth/me`, { withCredentials: true });
      setUser(data);
      return data;
    } catch (_) { /* ignore */ }
  };

  // Switch the active workspace for this browser session. Optionally persists
  // server-side via /api/workspaces/switch so future logins land in the same
  // place.
  const switchWorkspace = async (workspaceId, { persist = true } = {}) => {
    if (!workspaceId) return;
    setActiveWorkspaceId(workspaceId);
    try { localStorage.setItem(LS_WORKSPACE_KEY, workspaceId); } catch (_) { /* noop */ }
    _setAxiosWorkspaceHeader(workspaceId);
    if (persist) {
      try {
        await axios.post(`${API_URL}/api/workspaces/switch/${workspaceId}`, {}, { withCredentials: true });
      } catch (_) { /* ignore */ }
    }
    // Reload the current workspace details
    try {
      const { data } = await axios.get(`${API_URL}/api/workspaces/current`, { withCredentials: true });
      setActiveWorkspace(data?.workspace || null);
      setActivePlan(data?.plan || null);
    } catch (_) { /* ignore */ }
  };

  return (
    <AuthContext.Provider value={{
      user, loading,
      login, signup, logout, setUser, refreshUser,
      // Workspace state + helpers
      activeWorkspaceId, activeWorkspace, activePlan, myWorkspaces, switchWorkspace,
    }}>
      {children}
    </AuthContext.Provider>
  );
};
